import { IGeometrySearchParameter, IResult } from "@/types/map";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import distance from "@turf/distance";
import * as turf from "@turf/helpers";
import { reject, resolve } from "esri/core/promiseUtils";
import DrawOverlaysBD from "../../DrawOverlays/bd-gl/DrawOverlaysBDGL";
import { OverlayBaidu } from "../../Overlays/bd-gl/OverlayBaiduGL";

declare const BMapGL: any;
declare const BMapGLLib: any;

export default class GeometrySearchBD {
  private static intances: Map<string, any>;
  private view!: any;
  private searchTypes!: any;
  private animationPoints: any[] = [];
  private animationHandler: any;

  private _clickHandle: any;
  private _searchGeometry: any; //用于搜索的几何体
  private _addOverlayFunction: any;
  //用于几何搜索的符号样式
  private _searchSymbol = {
    type: "circle",
    opacity: 0.5,//填充透明度为0.5
    color: "#0080ff",
    outline: {
      color: "red",
      style: "dashed",
    }
  };

  //替换查找结果的图标
  private _replaceTargetIcon: any;
  private _replacedTargets: Map<any, any>; //被替换图标的覆盖物和原图标

  // private _searchGeo: any;

  private constructor(view: any) {
    this.view = view;
    this._replacedTargets = new Map<any, any>();
  }
  public static getInstance(view: any) {
    let id = view.getContainer().id;
    if (!GeometrySearchBD.intances) {
      GeometrySearchBD.intances = new Map();
    }
    let intance = GeometrySearchBD.intances.get(id);
    if (!intance) {
      intance = new GeometrySearchBD(view);
      GeometrySearchBD.intances.set(id, intance);
    }
    return intance;
  }
  public async clearGeometrySearch(): Promise<IResult> {
    const overlay = OverlayBaidu.getInstance(this.view);
    await overlay.deleteOverlays({ types: ["geometrySearch"] });
    this.view.removeEventListener("click", this._addOverlayFunction);

    const drawOverlays = DrawOverlaysBD.getInstance(this.view);
    await drawOverlays.deleteDrawOverlays({ types: ["geometrySearch"] });

    //还原覆盖物图标
    this._replacedTargets.forEach((icon: any, overlay: any) => {
      overlay.setIcon(icon);
    });
    this._replacedTargets.clear();
    this._replaceTargetIcon = undefined;

    this.searchTypes = [];
    this._searchGeometry = [];
    this._clickHandle = null;
    return {
      message: "成功调用该方法！",
      status: 0,
    };
  }
  public async nearGeometrySearch(params: IGeometrySearchParameter) {
    this.searchTypes = params.types || [];
    let returnResults = [];
    let pt = params.geometry; //geometry:[[]],{center:[],radius}
    let searchGeometry = new BMapGL.Point(pt.x, pt.y);
    let maxLen: number = params.maxLen || 10000000;
    let animation = params.animate !== false;
    const allOverlays = this.view.getOverlays();
    let inPolygonOverlays: any = {};
    allOverlays.forEach((overlay: any) => {
      if (
        this.searchTypes.length == 0 ||
        this.searchTypes.indexOf(overlay.type) > -1 ||
        (overlay.attributes &&
          this.searchTypes.indexOf(overlay.attributes.type) > -1)
      ) {
        //普通点位
        if (overlay instanceof BMapGL.Marker) {
          let pt = overlay.point;
          let dis = this.view.getDistance(pt, searchGeometry);
          if (inPolygonOverlays[overlay.type]) {
            let currdis = inPolygonOverlays[overlay.type].dis;
            if (currdis > dis) {
              inPolygonOverlays[overlay.type] = { dis, overlay };
            }
          } else {
            if (dis < maxLen) {
              inPolygonOverlays[overlay.type] = { dis, overlay };
            }
          }
        } else if (overlay instanceof BMapGL.PointCollection) {
          //海量点位
          let points = overlay._data.points;
          for (let point of points) {
            let dis = this.view.getDistance(point, searchGeometry);
            if (inPolygonOverlays[overlay.type]) {
              let currdis = inPolygonOverlays[overlay.type].dis;
              if (currdis > dis) {
                point.layer = overlay;
                inPolygonOverlays[overlay.type] = { dis, overlay: point };
              }
            } else {
              if (dis < maxLen) {
                point.layer = overlay;
                inPolygonOverlays[overlay.type] = { dis, overlay: point };
              }
            }
          }
        }
      }
    }, this);
    for (let type in inPolygonOverlays) {
      returnResults.push(inPolygonOverlays[type].overlay);
    }
    if (animation) {
      this.overlayAnimation(returnResults);
    }
    return inPolygonOverlays;
  }

  public async backgroundGeometrySearch(
    params: IGeometrySearchParameter
  ): Promise<IResult> {
    await this.clearGeometrySearch();

    this.searchTypes = params.types || [];
    let returnResults = [];
    const searchGeometry = params.geometry; //geometry:[[]],{center:[],radius}
    if (Array.isArray(searchGeometry)) {
      if (
        !(
          searchGeometry[0][0] ===
          searchGeometry[searchGeometry.length - 1][0] &&
          searchGeometry[0][1] === searchGeometry[searchGeometry.length - 1][1]
        )
      ) {
        //首尾点不等情况，默认将第一个点推进数组
        searchGeometry.push([searchGeometry[0][0], searchGeometry[0][1]]);
      }
      this._searchGeometry = this.getMarker(searchGeometry, "polygon");
    } else {
      if (searchGeometry.hasOwnProperty("center")) {
        this._searchGeometry = this.getMarker(searchGeometry, "circle");
      }
    }

    returnResults = await this.booleanContains();

    return {
      message: "no complete",
      status: 0,
      result: returnResults.length ? returnResults : null,
    };
  }

  private getMarker(geometry: any, type: string): any {
    let marker: any;
    switch (type) {
      case "rectangle":
      case "polygon":
        marker = new BMapGL.Polygon(geometry.coordinatesRaw);
        break;
      case "circle":
        let point = geometry.center instanceof Array ?
          new BMapGL.Point(geometry.center[0], geometry.center[1]) :
          new BMapGL.Point(geometry.center.x, geometry.center.y);
        marker = new BMapGL.Circle(
          point,
          geometry.radius
        );
        break;
    }
    return marker;
  }

  public async startGeometrySearch(
    params: IGeometrySearchParameter
  ): Promise<IResult> {
    const drawType = params.drawType || "point";//point是指以点为中的的圆形区域
    const pointsType = params.types || [];
    this.searchTypes = pointsType;
    this._clickHandle = params.clickHandle;

    //是否替换结果图标
    this._replaceTargetIcon = params.targetSymbol;

    //设置搜索的图形样式
    this._searchSymbol.type = drawType;
    if (params.drawSymbol) {
      let pd = params.drawSymbol;
      //适配arcgis的参数设置
      this._searchSymbol.color = pd.color instanceof Array ?
        ('rgb(' + pd.color[0] + ',' + pd.color[1] + ',' + pd.color[2] + ')')
        : pd.color;
      this._searchSymbol.opacity = ((pd.color instanceof Array) && (pd.color.length > 3)) ?
        pd.color[3] : 0.4;
      this._searchSymbol.outline.color = pd.outline.color instanceof Array ?
        ('rgb(' + pd.outline.color[0] + ',' + pd.outline.color[1] + ',' + pd.outline.color[2] + ')')
        : pd.outline.color;
      this._searchSymbol.type = drawType;
    }

    let resultPromise = new Promise<IResult>((resolve, reject) => {
      switch (drawType) {
        case "point":
          //几何搜索范围为以一点为中心的圆形区域
          this.doSearchByCircleRange(params)
            .then((successback) => {
              resolve(successback);
            })
            .catch((failback) => {
              reject(failback);
            });
          break;
        case "polygon":
        case "circle":
        case "rectangle":
          this.doSearchByDrawRange(params)
            .then((successback) => {
              resolve(successback);
            })
            .catch((failback) => {
              reject(failback);
            });
          break;
        default:
          reject({
            message: "drawType error!",
            status: -1,
          });
          break;
      }
    }).catch((rejectmsg) => {
      return rejectmsg;
    });
    return resultPromise;
  }

  private async doSearchByCircleRange(params: any): Promise<IResult> {
    return new Promise<IResult>((resolve, reject) => {
      ///用于搜索的几何图形
      let geo = {
        radius: params.radius || 2000,
        center: { x: undefined, y: undefined },
      };
      //向地图上添加几何图形的方法
      this._addOverlayFunction = (event: any) => {
        geo.center.x = event.point.lng;
        geo.center.y = event.point.lat;
        //根据指定半径添加圆到地图上
        const searchCirle = OverlayBaidu.getInstance(this.view);
        this._searchSymbol.type = "circle";
        let searchGeo = {
          defaultSymbol: this._searchSymbol,
          overlays: [{
            geometry: geo,
            type: "geometrySearch",
            symbol: this._searchSymbol,
          }],
          triggerMouseout: false,
          triggerMouseover: false,
          triggerClick: false,
          centerResult: true,
        };
        searchCirle.addOverlays(searchGeo);
        //用于搜索的几何体
        this._searchGeometry = this.getMarker(geo, "circle");
        //开始搜索
        this.geometrySearch({
          center: geo.center,
          radius: geo.radius,
          searchGeo: searchGeo,
        }).then((res: any) => {
          resolve(res);
        });
      };
      //有center参数无需与地图交互,否则通过鼠标点击地图获取
      if (params.center) {
        if (params.center instanceof Array) {
          this._addOverlayFunction({
            point: {
              lng: params.center[0],
              lat: params.center[1]
            }
          });
        } else {
          reject({
            message: "center is not a array！",
            status: -1,
          });
        }
      } else {
        this.view.addEventListener("click", this._addOverlayFunction);
      }
    }).catch((failback) => {
      return failback;
    });
  }

  private async doSearchByDrawRange(params: any): Promise<IResult> {
    //在图上绘制矩形、圆形、多边形区域作为几何搜索范围
    const drawOverlays = DrawOverlaysBD.getInstance(this.view);
    //每次绘制前把上次的绘制图形清除
    await drawOverlays.deleteDrawOverlays({ types: ["geometrySearch"] });

    return new Promise<IResult>((resolve, reject) => {
      drawOverlays.startDrawOverlays({
        drawType: params.drawType,
        type: "geometrySearch",
        clearLastResult: true,
        repeat: false,
        symbol: this._searchSymbol,
        callback: (res: any) => {
          ///@todo 绘制回调
          // console.log("draw result", res);
          let searchGeo = res.geometry;
          if (searchGeo.type === "circle") {
            this._searchGeometry = this.getMarker(searchGeo, "circle");
          } else if (searchGeo.type === "rectangle") {
            this._searchGeometry = this.getMarker(searchGeo, "rectangle");
          } else if (searchGeo.type === "polygon") {
            this._searchGeometry = this.getMarker(searchGeo, "polygon");
          } else {
            reject({
              message: "error searchGeo type!",
              status: -1,
            })
          }
          //开始搜索
          this.geometrySearch({
            center: searchGeo.center,
            radius: searchGeo.radius,
            searchGeo: searchGeo,
          }).then((res: any) => {
            resolve(res);
          });
        }
      });

    }).catch((failback) => {
      return failback;
    });
  }

  private async geometrySearch(params: any): Promise<IResult> {
    const returnResults = await this.booleanContains();

    //回调结果
    let searchResults = {
      center: params.center,
      radius: params.radius,
      geometry: params.searchGeo,
      searchResults: returnResults,
      searchGeometryJson: JSON.stringify({
        type: "geometrysearch",
        overlays: [JSON.stringify(params.searchGeo)],
      }),
      drawGeometryJson: JSON.stringify(params.graphic),
      searchPointJson: JSON.stringify({
        type: "geometrysearch",
        overlays: returnResults,
      }),
    };

    if (this._clickHandle) {
      this._clickHandle(searchResults);
    }

    return {
      message: "成功调用该方法！",
      status: 0,
      result: returnResults,
    };
  }

  public async startSearchByKey(
    params: IGeometrySearchParameter
  ): Promise<IResult> {
    let key = params.key;
    let allOverlays = this.view.getOverlays();
    const pointsType = params.types || [];
    let inPolygonOverlays: any[] = [];
    let viewpoints: any[] = [];
    let overlays = this.view.getOverlays();
    let currentOverlay: any;
    overlays.forEach((overlay: any) => {
      if (overlay.type == "geometrySearch") {
        currentOverlay = overlay;
      }
    });
    allOverlays.forEach((overlay: any) => {
      if (
        pointsType.length == 0 ||
        pointsType.indexOf(overlay.type) > -1 ||
        (overlay.attributes && pointsType.indexOf(overlay.attributes.type) > -1)
      ) {
        //普通点位
        if (overlay instanceof BMapGL.Marker) {
          if (
            currentOverlay == undefined ||
            (currentOverlay &&
              this.pointInGeometry(overlay.point, currentOverlay))
          ) {
            let attr = overlay.attributes;
            if (this.searchPoint(overlay.point, currentOverlay, key, attr)) {
              inPolygonOverlays.push(overlay);
              viewpoints.push(overlay.point);
            }
          }
        } else if (overlay instanceof BMapGL.PointCollection) {
          //海量点位
          let points = overlay._data.points;
          for (let point of points) {
            if (
              currentOverlay == undefined ||
              (currentOverlay && this.pointInGeometry(point, currentOverlay))
            ) {
              let attr = point.attributes;
              if (this.searchPoint(point, currentOverlay, key, attr)) {
                inPolygonOverlays.push(point);
                viewpoints.push(point);
              }
            }
          }
        }
      }
    }, this);
    if (viewpoints.length > 0) {
      let viewpoint = this.view.getViewport(viewpoints);
      await this.view.centerAndZoom(viewpoint.center, viewpoint.zoom);
    }

    if (inPolygonOverlays.length > 0) {
      this.overlayAnimation(inPolygonOverlays);
    }
    return { message: "search complete", status: 0, result: inPolygonOverlays };
  }
  private searchPoint(
    point: any,
    currentOverlay: any,
    key: string | undefined,
    attr: any
  ) {
    if (
      !currentOverlay ||
      (currentOverlay && this.pointInGeometry(point, currentOverlay))
    ) {
      if (key == undefined || key == "") {
        return true;
      } else {
        for (let field in attr) {
          if (typeof attr[field] == "string") {
            if (attr[field].toString().indexOf(key) > -1) {
              return true;
            }
          }
        }
      }
    }
    return false;
  }

  //@return {isIn,distance}
  private pointInGeometry(point: any, geometry: any, geo?: any): { isIn: boolean, distance?: any } {
    let contain: boolean = false;
    if (geometry instanceof BMapGL.Circle) {
      const radius = geometry.getRadius();
      const center = geometry.getCenter();
      const turfCenter = turf.point([center.lng, center.lat]);
      const turfPoint = turf.point([point.lng, point.lat]);
      const pointDistance = distance(turfPoint, turfCenter, {
        units: "meters",
      });
      return {
        isIn: pointDistance <= radius,
        distance: pointDistance
      };
    } else {
      const path = geometry.getPath();
      const line = path.map((point: any) => [point.lng, point.lat]);
      //把第一个点复制到最后形成闭环
      line.push(line[0]);
      const turfPolygon = turf.polygon([line]);
      const turfPoint = turf.point([point.lng, point.lat]);
      contain = booleanPointInPolygon(turfPoint, turfPolygon);
    }
    return { isIn: contain };
  }

  //@return {isIn,distance}
  private overlayInGeometry(overlay: any, geometry: any): { isIn: boolean, distance?: any } {
    let contain: boolean = false;
    if (geometry instanceof BMapGL.Circle) {
      return {
        isIn: BMapGLLib.GeoUtils.isPointInCircle(overlay, geometry),
        distance: BMapGLLib.GeoUtils.getDistance(overlay, geometry.getCenter()),
      };
    } else {
      if (BMapGLLib.GeoUtils.isPointInPolygon(overlay, geometry)) {
        contain = true;
      }
    }
    return { isIn: contain };
  }

  private async booleanContains(): Promise<any> {
    const allOverlays = this.view.getOverlays();
    let inPolygonOverlays: any[] = [];
    //逐一遍历判断
    allOverlays.forEach((overlay: any) => {
      if (
        this.searchTypes.length == 0 ||
        this.searchTypes.indexOf(overlay.type) > -1 ||
        (overlay.attributes &&
          this.searchTypes.indexOf(overlay.attributes.type) > -1)
      ) {
        //普通点位
        if (overlay instanceof BMapGL.Marker) {
          let ans = this.pointInGeometry(overlay.getPosition(), this._searchGeometry);
          if (ans.isIn) {
            overlay.distance = ans.distance;
            inPolygonOverlays.push(overlay);
          }
        } else if (overlay instanceof BMapGL.PointCollection) {
          ///////@todo 未经过测试
          //海量点位
          let points = overlay._data.points;
          for (let point of points) {
            let ans = this.pointInGeometry(point, this._searchGeometry);
            if (ans.isIn) {
              overlay.distance = ans.distance;
              inPolygonOverlays.push(overlay);
            }
          }
        }
      }
    }, this);

    //处理查找结果
    const returnResults: any = [];
    let pcPoints: any = [];
    inPolygonOverlays.forEach((overlay: any) => {
      let returnOverlay: any = {};
      returnOverlay.attributes = overlay.attributes;
      returnOverlay.type = overlay.type || overlay.attributes.type || undefined;
      returnOverlay.id = overlay.id || overlay.attributes.id || undefined;
      returnOverlay.geometry = overlay.point || overlay.getPosition();
      returnOverlay.distance = overlay.distance;
      returnOverlay.x = overlay.getPosition().lng;
      returnOverlay.y = overlay.getPosition().lat;

      if (overlay instanceof BMapGL.Point) {
        pcPoints.push(overlay);
      }
      returnResults.push(returnOverlay);

      if (overlay instanceof BMapGL.Marker) {
        //替换图标
        if (this._replaceTargetIcon) {
          let icon_old = overlay.getIcon();
          if (icon_old) {
            let icon_new = new BMapGL.Icon(icon_old.imageUrl, icon_old.size);
            icon_new.anchor = icon_old.anchor;
            icon_new.size = icon_old.size;
            icon_new.imageOffset = icon_old.imageOffset;
            icon_new.imageSize = icon_old.imageSize;
            icon_new.imageUrl = icon_old.imageUrl;
            icon_new.infoWindowAnchor = icon_old.infoWindowAnchor;
            icon_new.printImageUrl = icon_old.printImageUrl;

            let rti = this._replaceTargetIcon;
            icon_new.setImageUrl(rti.url);
            icon_new.setSize(new BMapGL.Size(
              Number(rti.size instanceof Array ? rti.size[0] : rti.size) / 0.75,
              Number(rti.size instanceof Array ? rti.size[1] : rti.size) / 0.75
            ));
            icon_new.setImageSize(new BMapGL.Size(
              Number(rti.width) / 0.75,
              Number(rti.height) / 0.75
            ));

            overlay.setIcon(icon_new);

            this._replacedTargets.set(overlay, icon_old); //留存
          }

          ////BMapGL v1.0中没有setAnimation设置动画的方法
          // overlay.setAnimation(2); 
          // setTimeout(() => {
          //   overlay.setAnimation(0);
          // }, 2500);
        }
      }
    });
    if (pcPoints.length > 0) {
      this.overlayAnimation(pcPoints);
    }
    return returnResults;
  }

  public async overlayAnimation(overlays: any) {
    let layerType: string = "";
    let layer: any;
    if (this.animationPoints && this.animationPoints.length > 0) {
      this.overlayAnimationClear(this.animationPoints);
      if (this.animationHandler) {
        clearTimeout(this.animationHandler);
      }
    }

    let _this = this;
    this.animationPoints = overlays.map((overlay: any) => {
      let point: any;
      if (overlay instanceof BMapGL.Point) {
        overlay.visible = false;
        if (layerType !== overlay.type) {
          if (layer) {
            layer.draw();
          }
          layer = overlay.layer;
        }
        layerType = overlay.type;
        point = new BMapGL.Marker(overlay, overlay.icon); //海量点
        point.isPoint = true;
        point.Pcpoint = overlay;
        _this.view.addOverlay(point); // 将标注添加到地图中
      } else {
        point = overlay;
      }
      point.setAnimation(2); //跳动的动画
      return point;
    }, this);
    if (layer) {
      layer.draw();
    }
    layerType = "";
    layer = undefined;

    this.animationHandler = setTimeout(() => {
      _this.overlayAnimationClear(_this.animationPoints);
    }, 2500);
  }

  public async overlayAnimationClear(points: any[]) {
    let layerType: string = "";
    let layer: any;
    points.forEach((overlay: any) => {
      if (overlay.isPoint) {
        this.view.removeOverlay(overlay);
        overlay.Pcpoint.visible = true;
        if (layerType !== overlay.Pcpoint.type) {
          if (layer) {
            layer.draw();
          }
          layer = overlay.Pcpoint.layer;
        }
        layerType = overlay.Pcpoint.type;
      } else {
        overlay.setAnimation(0);
      }
    });
    if (layer) {
      layer.draw();
    }

    layerType = "";
    layer = undefined;
    this.animationPoints = [];
    this.animationHandler = undefined;
  }

}
